// Dateien aus dem Betriebssystem (Windows-Explorer, Finder) in den Explorer
// ziehen (User 22.10.2026: „Drag & Drop zum Hochladen muss funktionieren").
//
// DOM-nah, aber ohne React: die Komponente reicht nur den `DataTransfer`
// herein und bekommt eine Liste fürs Hochladen zurück.

/** Eine abgelegte Datei — `relativePath` nur, wenn sie aus einem Ordner stammt. */
export type AbgelegteDatei = { file: File; relativePath?: string };

/**
 * Zieht gerade jemand Dateien vom Rechner herein? Während `dragover` liefert
 * der Browser nur die Typen. Ein Zug innerhalb von KMVS (Verschieben im
 * Explorer, Dokument/Mail aus dem Vorgang) trägt eigene Typen — Firefox meldet
 * dabei mitunter zusätzlich `Files`, deshalb gewinnt die KMVS-Marke.
 */
export function ziehtDateien(typen: readonly string[] | undefined): boolean {
  const liste = typen ?? [];
  return liste.includes("Files") && !liste.some((t) => t.startsWith("application/x-kmvs"));
}

/** Minimal, was hier von der FileSystem-API gebraucht wird. */
type Eintrag = {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  fullPath: string;
  file?: (ok: (f: File) => void, fehler?: (e: unknown) => void) => void;
  createReader?: () => { readEntries: (ok: (e: Eintrag[]) => void, fehler?: (e: unknown) => void) => void };
};

const dateiAus = (e: Eintrag): Promise<File> =>
  new Promise((ok, fehler) => (e.file ? e.file(ok, fehler) : fehler(new Error("keine Datei"))));

const alleEintraege = async (ordner: Eintrag): Promise<Eintrag[]> => {
  const leser = ordner.createReader?.();
  if (!leser) return [];
  const alle: Eintrag[] = [];
  // readEntries liefert in Portionen (Chrome: 100) — bis zur leeren Antwort lesen.
  for (;;) {
    const teil = await new Promise<Eintrag[]>((ok, fehler) => leser.readEntries(ok, fehler));
    if (teil.length === 0) break;
    alle.push(...teil);
  }
  return alle;
};

const sammle = async (e: Eintrag, ausOrdner: boolean, ziel: AbgelegteDatei[]): Promise<void> => {
  if (e.isFile) {
    const file = await dateiAus(e);
    const relativePath = ausOrdner ? e.fullPath.replace(/^\/+/, "") : undefined;
    ziel.push(relativePath ? { file, relativePath } : { file });
    return;
  }
  if (e.isDirectory) {
    for (const kind of await alleEintraege(e)) await sammle(kind, true, ziel);
  }
};

/**
 * Die abgelegten Dateien — ganze Ordner rekursiv, mit ihrem Pfad, damit sie in
 * der Akte als Ordner wieder auftauchen (wie „Ordner hochladen").
 *
 * WICHTIG: Muss SYNCHRON im `drop`-Handler aufgerufen werden — nach dem
 * ersten `await` gibt der Browser `items` nicht mehr heraus. Die Einträge
 * werden deshalb vor jeder Wartezeit eingesammelt.
 */
export function dateienAusDrop(dt: DataTransfer): Promise<AbgelegteDatei[]> {
  const items = Array.from(dt.items ?? []).filter((i) => i.kind === "file");
  const eintraege = items.map((i) => {
    const get = (i as DataTransferItem & { webkitGetAsEntry?: () => Eintrag | null }).webkitGetAsEntry;
    return { eintrag: get ? get.call(i) : null, datei: i.getAsFile() };
  });
  const mitOrdner = eintraege.some((x) => x.eintrag?.isDirectory);
  if (!mitOrdner) {
    const dateien = Array.from(dt.files ?? []);
    return Promise.resolve(dateien.map((file) => ({ file })));
  }
  return (async () => {
    const ziel: AbgelegteDatei[] = [];
    for (const x of eintraege) {
      if (x.eintrag) await sammle(x.eintrag, false, ziel);
      else if (x.datei) ziel.push({ file: x.datei });
    }
    return ziel;
  })();
}
